import { alpha, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import { ReactNode } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';
import { NavLink } from 'react-router-dom';

interface AdminSidebarLinkProps {
  to: string;
  message?: MessageDescriptor;
  /** Brand or already-translated label when `message` is omitted. */
  label?: string;
  icon: ReactNode;
  /** Replaces NavLink prefix matching when the section path is not a prefix of `to`. */
  isActive?: (pathname: string) => boolean;
}

/**
 * One row in the admin column: monochrome icon, sentence-case label, soft fill when active.
 */
export function AdminSidebarLink({ to, message, label, icon, isActive }: AdminSidebarLinkProps) {
  const { $t } = useIntl();
  const text = message ? $t(message) : label;

  return (
    <ListItemButton
      component={NavLink}
      to={to}
      isActive={isActive ? (_, location) => isActive(location.pathname) : undefined}
      disableRipple
      sx={{
        borderRadius: 1,
        px: 1,
        py: 0.5,
        minHeight: 34,
        color: 'text.primary',
        // Bootstrap `a:hover` is more specific than a single class and paints link-blue
        // onto the anchor; the label inherits it. Re-assert on :hover so the class:hover wins.
        '&:hover': {
          color: 'text.primary',
        },
        '& .MuiListItemIcon-root, &:hover .MuiListItemIcon-root': {
          color: 'text.secondary',
          minWidth: 28,
        },
        '&.active, &.active:hover': {
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
          // Primary cyan is an accent, not body text — keep the label readable on light surfaces.
          color: 'text.primary',
          '& .MuiListItemIcon-root': { color: 'primary.main' },
          '& .MuiListItemText-primary': { fontWeight: 500 },
        },
      }}
    >
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText primary={text} primaryTypographyProps={{ variant: 'body2' }} />
    </ListItemButton>
  );
}
